<template>
  <div class="account-login booking-container">
    <div class="has-shadow mb-5 w-100">
      <div class="row">
        <div class="col-24 col-xl-12 bg-slate-blue account-register-promotion">
          <div class="pt-4 pb-8 pt-xl-8 px-7">
            <div class="mb-5">
              <span v-if="isUpdate" class="sub-heading">Update your</span>
              <span v-else class="sub-heading">Create a</span>

              <h3>Rockliffe Account</h3>
              <p>You'll miss out on these account benefits:</p>

              <ul>
                <li>View all your bookings in one place</li>
                <li>Easily amend your booking</li>
                <li>Upgrade your stay</li>
              </ul>

              <div v-if="!isUpdate">
                <h4>Already registered?</h4>

                <div class="btn--container">
                  <router-link
                    class="btn btn--transparent btn--highlight"
                    v-bind:to="{ name: 'account_login' }"
                  >
                    Login Now
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-24 col-xl-12 bd-r bg-white">
          <div class="p-4 pt-xl-8 px-4 pb-xl-7 py-xl-8">
            <div id="error-box">
              <div v-if="error" class="mb-3">
                <info-box v-bind:type="'error'">
                  <template v-slot:body>
                    <span class="heading"> Error </span>

                    <div v-if="errorText.length">
                      <p>
                        {{ errorText }}
                      </p>
                    </div>
                  </template>
                </info-box>
              </div>
            </div>

            <div v-if="showOTP">
              <Form @submit="onSubmitOTP" :validation-schema="schemaOTP">
                <div class="form-group row mb-4">
                  <p>
                    Please enter the 6-digit code that we sent to your email
                    address.
                  </p>
                  <div class="form-group row mb-4">
                    <div class="col-24">
                      <div class="details-label">6 digit code</div>
                      <Field name="code" class="form-control"></Field>
                      <ErrorMessage name="code" class="error-message" />
                    </div>
                  </div>
                </div>

                <div class="form-group row mt-5">
                  <div class="col-24">
                    <div class="btn-container-start">
                      <div class="d-flex flex-row">
                        <button
                          v-bind:disabled="isLoading"
                          class="btn btn--gold"
                          type="submit"
                        >
                          Continue
                        </button>
                        <div
                          v-if="isLoading"
                          style="max-height: 66px; margin-left: 10px"
                        >
                          <loader></loader>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div v-else>
              <Form @submit="onSubmit" :validation-schema="schema">
                <div class="row">
                  <div class="col-24">
                    <div class="form-group row mb-4">
                      <div class="col-24 col-xl-6 mb-4 mb-xl-0">
                        <div class="details-label">Title</div>
                        <Field
                          name="title"
                          as="select"
                          class="form-select"
                          style="height: 57px"
                        >
                          <option
                            v-for="title in titles"
                            v-bind:key="title.OperaCode"
                            v-bind:value="title.OperaCode"
                          >
                            {{ title.DisplayName }}
                          </option>
                        </Field>
                        <ErrorMessage name="title" class="error-message" />
                      </div>

                      <div class="col-24 col-xl-9 mb-4 mb-xl-0">
                        <div class="details-label">First Name</div>
                        <Field
                          name="firstName"
                          class="form-control"
                          v-model="firstName"
                        ></Field>
                        <ErrorMessage name="firstName" class="error-message" />
                      </div>

                      <div class="col-24 col-xl-9 mb-xl-0">
                        <div class="details-label">Last Name</div>
                        <Field
                          name="lastName"
                          class="form-control"
                          v-model="lastName"
                        ></Field>
                        <ErrorMessage name="lastName" class="error-message" />
                      </div>
                    </div>

                    <div class="form-group row mb-4">
                      <div class="col-24">
                        <div class="details-label">Email</div>
                        <Field
                          name="emailAddress"
                          class="form-control"
                          v-model="emailAddress"
                        ></Field>
                        <ErrorMessage
                          name="emailAddress"
                          class="error-message"
                        />
                      </div>
                    </div>

                    <div class="form-group row mb-4">
                      <div class="col-24">
                        <div class="details-label">Telephone (optional)</div>
                        <TelephoneInput v-model="telephone"></TelephoneInput>

                        <ErrorMessage name="telephone" class="error-message" />
                      </div>
                    </div>

                    <div class="form-group row mb-4">
                      <div class="col-24 col-xl-16">
                        <div class="details-label">
                          When is your birthday? (optional)
                        </div>

                        <v-date-picker
                          class="inline-block h-full"
                          v-model="date"
                        >
                          <template v-slot="{ inputValue, togglePopover }">
                            <div
                              class="flex items-center"
                              @click="togglePopover()"
                            >
                              <input
                                v-bind:value="inputValue"
                                class="form-control search-date calendar"
                                readonly
                              />
                            </div>
                          </template>
                        </v-date-picker>
                        <Field
                          type="hidden"
                          v-model="date"
                          name="birthday"
                        ></Field>

                        <ErrorMessage name="birthday" class="error-message" />
                      </div>
                    </div>

                    <div class="form-group row mb-4">
                      <div class="col-24">
                        <div class="details-label">Set a password</div>
                        <ul>
                          <li>Minimum 8 characters</li>
                        </ul>
                        <Field
                          name="password"
                          class="form-control"
                          type="password"
                        ></Field>
                        <ErrorMessage name="password" class="error-message" />
                      </div>
                    </div>

                    <div class="row form-group mb-3">
                      <div class="col-24">
                        <label class="terms-label">
                          <Field
                            name="mailinglist"
                            type="checkbox"
                            value="true"
                            v-on:click="toggleMarketing()"
                          ></Field>
                          Join the mailing list (optional)
                        </label>
                        <ErrorMessage
                          name="mailinglist"
                          class="error-message"
                        />
                      </div>
                    </div>

                    <div class="row form-group mb-3" v-if="showMarketingBoxes">
                      <div class="col-xl-8">
                        <label class="terms-label">
                          <Field
                            name="addressBooks"
                            type="checkbox"
                            value="3507983"
                          ></Field>
                          Spa
                        </label>
                      </div>

                      <div class="col-xl-8">
                        <label class="terms-label">
                          <Field
                            name="addressBooks"
                            type="checkbox"
                            value="3508013"
                          ></Field>
                          Golf
                        </label>
                      </div>

                      <div class="col-xl-8">
                        <label class="terms-label">
                          <Field
                            name="addressBooks"
                            type="checkbox"
                            value="3508001"
                          ></Field>
                          Dining
                        </label>
                      </div>

                      <div class="col-xl-8">
                        <label class="terms-label">
                          <Field
                            name="addressBooks"
                            type="checkbox"
                            value="3508068"
                          ></Field>
                          Events
                        </label>
                      </div>

                      <div class="col-xl-8">
                        <label class="terms-label">
                          <Field
                            name="addressBooks"
                            type="checkbox"
                            value="3507997"
                          ></Field>
                          Rooms
                        </label>
                      </div>

                      <div class="col-xl-8">
                        <label class="terms-label">
                          <Field
                            name="addressBooks"
                            type="checkbox"
                            value="21980355"
                          ></Field>
                          Offers
                        </label>
                      </div>
                    </div>

                    <div class="row form-group">
                      <div class="col-24">
                        <label class="terms-label">
                          <Field
                            name="terms"
                            type="checkbox"
                            value="true"
                          ></Field>
                          I agree to the
                          <a href="/privacy-cookie-policy/" target="_blank"
                            >privacy notice</a
                          >
                          and
                          <a href="/general-booking-terms/" target="_blank"
                            >terms of use</a
                          >
                        </label>
                        <ErrorMessage name="terms" class="error-message" />
                      </div>
                    </div>

                    <div class="form-group row mt-5">
                      <div class="col-24">
                        <div class="btn-container-start">
                          <div class="d-flex flex-row">
                            <button
                              v-bind:disabled="isLoading"
                              class="btn btn--gold"
                              type="submit"
                            >
                              Register
                            </button>
                            <div
                              v-if="isLoading"
                              style="max-height: 66px; margin-left: 10px"
                            >
                              <loader></loader>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Field, Form, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import { accountStore } from "../store/index";
import Loader from "@/shared/components/Loader.vue";
import TelephoneInput from "@/shared/components/TelephoneInput.vue";
import InfoBox from "@/shared/components/InfoBox.vue";
import accountapi from "@/shared/api/account";

export default {
  name: "AccountLogin",
  mounted() {
    if (this.accountConfiguration == null) {
      accountStore.dispatch("getConfiguration");
    }
  },
  components: {
    Field,
    Form,
    ErrorMessage,
    Loader,
    InfoBox,
    TelephoneInput,
  },
  computed: {
    isUpdate() {
      if (
        this.updateEmailAddress != undefined &&
        this.updateEmailAddress != null &&
        this.updateEmailAddress.length
      ) {
        return true;
      }
      return false;
    },
    updateEmailAddress() {
      return this.$route.query.email;
    },
    showOTP() {
      return this.isUpdate && !this.isValidated;
    },

    configuration() {
      return accountStore.getters.accountConfiguration;
    },
    titles() {
      if (this.configuration != null) {
        return this.configuration.Titles;
      }
      return [];
    },
  },
  data() {
    const schemaOTP = yup.object().shape({
      code: yup
        .string()
        .required()
        .min(6, "Must be exactly 6 digits")
        .max(6, "Must be exactly 6 digits")
        .label("6 digit code"),
    });

    const schema = yup.object().shape({
      title: yup.string().required().label("Title"),
      firstName: yup.string().required().label("First name"),
      lastName: yup.string().required().label("Last name"),
      emailAddress: yup.string().required().email().label("Email address"),
      telephone: yup.string().notRequired().label("Telephone"),
      birthday: yup.string().nullable().notRequired().label("Birthday"),
      password: yup.string().notRequired().label("Passsword"),
      terms: yup
        .boolean()
        .required()
        .label("Privacy notice and terms and conditions"),
      addressBooks: yup.array().of(yup.string()),
    });

    return {
      error: false,
      errorText: "",
      isLoading: false,
      schema,
      schemaOTP,
      isValidated: false,
      date: null,
      firstName: null,
      lastName: null,
      emailAddress: null,
      showMarketingBoxes: false,
    };
  },
  methods: {
    toggleMarketing() {
      this.showMarketingBoxes = !this.showMarketingBoxes;
    },
    onSubmit(values) {
      if (!this.showMarketingBoxes) {
        values.addressBooks = null;
      }
      var errorBox = document.getElementById("error-box");
      this.loading = true;
      accountStore
        .dispatch("register", values)
        .then(() => {
          this.loading = false;
          this.$router.push({
            name: "account_login",
            query: { created: true },
          });
        })
        .catch((data) => {
          this.loading = false;
          this.errorText = data.error;
          this.error = true;
          window.scrollTo(errorBox.getBoundingClientRect().top, 0);
        });
    },

    onSubmitOTP(values) {
      this.isLoading = true;

      let payload = {
        emailAddress: this.updateEmailAddress,
        code: values.code,
      };

      accountapi.validateMigration(
        payload,
        (s) => {
          this.error = false;
          this.errorText = "";
          this.firstName = s.FirstName;
          this.lastName = s.LastName;
          this.emailAddress = s.EmailAddress;

          this.isValidated = true;
          this.isLoading = false;
        },
        (f) => {
          this.error = true;

          this.errorText = "Please check your details.";
          this.isLoading = false;
        }
      );
    },
  },
};
</script>

<style>
.account-login {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: auto !important;
  margin-top: 5%;
  margin-bottom: 5%;
}

.account-register-promotion {
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
}

.account-register-promotion * {
  color: white !important;
}

.account-register-promotion .sub-heading,
.account-register-promotion p {
  text-transform: uppercase;
  letter-spacing: 4px;
  font-family: "Playfair Display";
  font-size: 14px;
}

.account-register-promotion p {
  margin-bottom: 40px;
}

.account-register-promotion h3 {
  position: relative;
  margin-bottom: 140px;
  font-size: 36px;
  font-family: "Playfair Display";
}

.account-register-promotion h3:after {
  content: "";
  display: block;
  background-color: #a18f5b;
  width: 1px;
  height: 60px;
  bottom: -100px;
  left: 50%;
  position: absolute;
}

.account-register-promotion h4 {
  font-size: 24px;
  margin-top: 20px;
  margin-bottom: 20px;
  font-family: "Playfair Display";
}

.account-register-promotion ul {
  padding-left: 0;
  list-style-position: inside;
}

.account-register-promotion ul li {
  font-family: Montserrat;
  font-size: 14px;
  margin-bottom: 20px;
  position: relative;
  left: -20px;
}

.btn-golf {
  border-color: #425260;
}

.telephone-input {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
}

.country-code-dropdown {
  width: 30%;
}

@media (max-width: 1999px) {
  .account-login {
    display: block !important;
  }
}
</style>
